import * as THREE from "three";
import AppData from "../app.data";

export type AnimationRegistryEntry = {
  mixer: THREE.AnimationMixer;
  root: THREE.Object3D;
  slotTarget?: number;
  disposed?: boolean;
};

type RuntimeCleanupScope = {
  slotTarget?: number;
  root?: THREE.Object3D | null;
};

const appData = new AppData();

export function getAnimationMixer(entry: AnimationRegistryEntry | THREE.AnimationMixer): THREE.AnimationMixer | null {
  if (!entry) return null;
  return (entry as AnimationRegistryEntry).mixer || (entry as THREE.AnimationMixer);
}

export function getAnimationRoot(entry: AnimationRegistryEntry | THREE.AnimationMixer): THREE.Object3D | null {
  if (!entry) return null;
  const trackedEntry = entry as AnimationRegistryEntry;
  if (trackedEntry.root) return trackedEntry.root;

  const mixer = entry as THREE.AnimationMixer;
  return typeof (mixer as any).getRoot === "function" ? (mixer as any).getRoot() : null;
}

export function inferSlotTarget(object: THREE.Object3D | null | undefined): number | undefined {
  let current: THREE.Object3D | null | undefined = object;
  while (current) {
    const data = current.userData || {};
    const candidate = data.index ?? data.slotTarget ?? data.artworkWrapper?.slotTarget;
    if (Number.isInteger(candidate)) {
      return Number(candidate);
    }
    current = current.parent;
  }
  return undefined;
}

export function createAnimationEntry(
  mixer: THREE.AnimationMixer,
  root: THREE.Object3D,
  slotTarget?: number
): AnimationRegistryEntry {
  return {
    mixer,
    root,
    slotTarget,
    disposed: false,
  };
}

export function isObjectInActiveScene(object: THREE.Object3D | null | undefined): boolean {
  if (!object) return false;
  let current: THREE.Object3D | null = object;
  while (current) {
    if (current === appData.artworksScene || current === appData.spaceScene) return true;
    current = current.parent;
  }
  return false;
}

function matchesScope(root: THREE.Object3D | null, slotTarget: number | undefined, scope?: RuntimeCleanupScope) {
  if (!scope || (scope.slotTarget === undefined && !scope.root)) return true;
  if (scope.root && root) {
    let current: THREE.Object3D | null = root;
    while (current) {
      if (current === scope.root) return true;
      current = current.parent;
    }
  }
  return scope.slotTarget !== undefined && slotTarget === scope.slotTarget;
}

function isFullCleanup(scope?: RuntimeCleanupScope) {
  return !scope || (scope.slotTarget === undefined && !scope.root);
}

function shouldRemoveRoot(root: THREE.Object3D | null, slotTarget: number | undefined, scope?: RuntimeCleanupScope) {
  if (matchesScope(root, slotTarget, scope)) return true;
  return isFullCleanup(scope) && !isObjectInActiveScene(root);
}

export function cleanupAnimationEntry(entry: AnimationRegistryEntry | THREE.AnimationMixer) {
  const mixer = getAnimationMixer(entry);
  const root = getAnimationRoot(entry);
  if (!mixer) return;

  try {
    mixer.stopAllAction();
    if (root && typeof (mixer as any).uncacheRoot === "function") {
      (mixer as any).uncacheRoot(root);
    }
  } catch (error) {
    console.warn("Failed to clean up animation mixer", error);
  }

  if ((entry as AnimationRegistryEntry).mixer) {
    (entry as AnimationRegistryEntry).disposed = true;
  }
}

function cleanupVideoEntry(entry: any) {
  const video = entry?.video as HTMLVideoElement | undefined;
  if (!video) return;

  try {
    video.pause();
    video.removeAttribute("src");
    video.load();
  } catch (error) {
    console.warn("Failed to clean up artwork video", error);
  }
  entry.disposed = true;
}

function cleanupSoundEntry(entry: any) {
  const soundService = appData.main3DManager?.soundService;
  if (soundService && typeof soundService.cleanupRuntimeSound === "function") {
    soundService.cleanupRuntimeSound(entry);
    return;
  }

  try {
    entry?.stop?.();
    entry?.disconnect?.();
    entry?.parent?.remove?.(entry);
    entry?.removeFromParent?.();
    entry?.setBuffer?.(null);
  } catch (error) {
    console.warn("Failed to clean up artwork sound", error);
  }
}

function shouldRemoveObject(object: THREE.Object3D | null | undefined, scope?: RuntimeCleanupScope) {
  const root = object || null;
  return shouldRemoveRoot(root, inferSlotTarget(root), scope);
}

export function cleanupArtworkRuntimeRegistries(scope?: RuntimeCleanupScope) {
  appData.restoreViewerRotation?.();

  appData.skinnedMeshes = (appData.skinnedMeshes || []).filter((entry: AnimationRegistryEntry | THREE.AnimationMixer) => {
    const root = getAnimationRoot(entry);
    const slotTarget = (entry as AnimationRegistryEntry).slotTarget ?? inferSlotTarget(root);
    const shouldRemove = shouldRemoveRoot(root, slotTarget, scope);
    if (shouldRemove) cleanupAnimationEntry(entry);
    return !shouldRemove;
  });

  appData.videoList = (appData.videoList || []).filter((entry: any) => {
    const root = entry?.artworkModel || entry?.root;
    const slotTarget = entry?.slotTarget ?? inferSlotTarget(root);
    const shouldRemove = shouldRemoveRoot(root || null, slotTarget, scope);
    if (shouldRemove) cleanupVideoEntry(entry);
    return !shouldRemove;
  });

  appData.soundList = (appData.soundList || []).filter((entry: any) => {
    const root = entry?.userData?.loadedMesh || entry;
    const shouldRemove = shouldRemoveObject(root, scope);
    if (shouldRemove) cleanupSoundEntry(entry);
    return !shouldRemove;
  });

  appData.lookAtCameraModels = (appData.lookAtCameraModels || []).filter((model: THREE.Object3D) => {
    return !shouldRemoveObject(model, scope);
  });

  appData.emissionModels = (appData.emissionModels || []).filter((model: THREE.Object3D) => {
    return !shouldRemoveObject(model, scope);
  });
}

export function getRuntimeRegistryCounts() {
  return {
    videos: appData.videoList?.length || 0,
    sounds: appData.soundList?.length || 0,
    lookAtCameraModels: appData.lookAtCameraModels?.length || 0,
    skinnedMeshes: appData.skinnedMeshes?.length || 0,
    emissionModels: appData.emissionModels?.length || 0,
  };
}
